import { useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactElement } from "react";
import { ArrowRight, Check, FlaskConical, KeyRound, Link2, Mail, Users } from "lucide-react";
import { storePendingInvite } from "../../account/pendingInvite";
import { API_BASE_URL, BRAND } from "../../brand";
import { Button } from "./Button";
import { Field } from "./Field";

export type AccessMode = "public_waitlist" | "invite_beta" | "redeem";

type SubmitState = "idle" | "submitting" | "success" | "duplicate" | "error";

type WaitlistExperienceProps = {
  mode: AccessMode;
  setMode: (mode: AccessMode) => void;
};

const accessModes: AccessMode[] = ["public_waitlist", "invite_beta", "redeem"];


function AccessSignupForm({ mode }: { mode: Exclude<AccessMode, "redeem"> }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<SubmitState>("idle");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const alertRef = useRef<HTMLDivElement>(null);

  const reportFormError = (message: string) => {
    setFormError(message);
    requestAnimationFrame(() => alertRef.current?.focus());
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setEmailError("Enter a complete email address, such as name@example.com.");
      setFormError("");
      return;
    }
    if (!consent) {
      setEmailError("");
      reportFormError("Confirm that we may email you about this access request.");
      return;
    }

    setEmailError("");
    setFormError("");
    setState("submitting");
    try {
      const response = await fetch(`${API_BASE_URL}/v1/access-signups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
          cohort: mode,
          consent,
          source: "landing_page",
          website,
        }),
      });
      const payload = await response.json().catch(() => ({})) as { duplicate?: boolean; error?: { message?: string } };
      if (response.ok) {
        setState(payload.duplicate ? "duplicate" : "success");
        return;
      }
      setState("error");
      reportFormError(response.status === 429
        ? "Too many attempts. Wait a few minutes and try again."
        : payload.error?.message ?? "We could not save your request. Try again.");
    } catch {
      setState("error");
      reportFormError("We could not reach the signup service. Check your connection and try again.");
    }
  };

  if (state === "success" || state === "duplicate") {
    return (
      <div className="access-success" role="status">
        <span><Check aria-hidden="true" size={24} /></span>
        <h3>{state === "duplicate" ? "You’re already on the list." : "Your place is recorded."}</h3>
        <p>{state === "duplicate"
          ? <>Your confirmed signup for <strong>{email}</strong> is already recorded. No new email was sent.</>
          : <>We sent a confirmation to <strong>{email}</strong>. Keep an eye on your inbox and spam folder for access updates.</>}</p>
        <button onClick={() => setState("idle")}>Use another email</button>
      </div>
    );
  }

  return (
    <form className="access-form" noValidate onSubmit={submit}>
      <div aria-hidden="true" className="access-form__honeypot" hidden>
        <label>Website<input aria-hidden="true" autoComplete="off" name="website" onChange={(event) => setWebsite(event.target.value)} tabIndex={-1} value={website} /></label>
      </div>
      <Field
        autoComplete="email"
        error={emailError}
        icon={<Mail size={17} />}
        label="Email address"
        name="email"
        onChange={(event) => setEmail(event.target.value)}
        placeholder="you@example.com"
        type="email"
        value={email}
      />
      <label className="consent">
        <input checked={consent} onChange={(event) => setConsent(event.target.checked)} type="checkbox" />
        <span aria-hidden="true" className="consent__box"><Check size={12} /></span>
        <span>Email me a confirmation and updates about this access request.</span>
      </label>
      {formError && <div className="access-form__alert" ref={alertRef} role="alert" tabIndex={-1}>{formError}</div>}
      <Button disabled={state === "submitting"} fullWidth type="submit">
        {state === "submitting" ? "Recording your request…" : mode === "public_waitlist" ? "Join the public waitlist" : "Request beta access"}
        <ArrowRight aria-hidden="true" size={17} />
      </Button>
      <p className="access-form__privacy">We use your email only for this access request. <a href="/access-request-email.html">How we handle it</a>.</p>
    </form>
  );
}

function RedeemInviteForm() {
  const [code, setCode] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "error">("idle");
  const [codeError, setCodeError] = useState("");
  const [formError, setFormError] = useState("");
  const alertRef = useRef<HTMLDivElement>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (code.trim().length < 12) {
      setCodeError("Enter the complete invite code.");
      return;
    }

    setState("submitting");
    setCodeError("");
    setFormError("");
    try {
      const response = await fetch(`${API_BASE_URL}/v1/invites/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: code.trim() }),
      });
      const payload = await response.json().catch(() => ({})) as { email?: string | null; error?: { code?: string; message?: string } };
      if (!response.ok) {
        const message = response.status === 429
          ? "Too many invite checks were made from this connection. Wait 15 minutes and try again."
          : response.status === 404 || response.status === 409
            ? "That invite is invalid, expired, or already used. Check the code and try again."
            : response.status >= 500
              ? "The account service is temporarily unavailable. Your invite was not used; try again shortly."
              : payload.error?.message ?? "The invite could not be checked. Review the code and try again.";
        setState("error");
        setFormError(message);
        requestAnimationFrame(() => alertRef.current?.focus());
        return;
      }
      storePendingInvite({ code: code.trim(), email: payload.email ?? null });
      window.location.assign("/signup");
    } catch {
      setState("error");
      setFormError("The account service could not be reached. Check your connection; your invite was not used.");
      requestAnimationFrame(() => alertRef.current?.focus());
    }
  };

  return (
    <form className="access-form access-form--redeem" noValidate onSubmit={submit}>
      <Field autoComplete="one-time-code" error={codeError} icon={<KeyRound size={17} />} label="Invite code" name="invite-code" onChange={(event) => setCode(event.target.value)} placeholder="M4M-••••-••••" value={code} />
      {formError && <div className="access-form__alert" ref={alertRef} role="alert" tabIndex={-1}>{formError}</div>}
      <Button disabled={state === "submitting"} fullWidth type="submit">
        {state === "submitting" ? "Checking invite…" : "Continue to account setup"}<ArrowRight aria-hidden="true" size={17} />
      </Button>
      <p className="access-form__privacy">Already created your account? <a href="/login">Log in</a>.</p>
    </form>
  );
}

export function WaitlistExperience({ mode, setMode }: WaitlistExperienceProps): ReactElement {
  const baseId = useId();
  const tabId = (value: AccessMode) => `${baseId}-${value}`;
  const moveTab = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const current = accessModes.indexOf(mode);
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? accessModes.length - 1
        : (current + (event.key === "ArrowRight" ? 1 : -1) + accessModes.length) % accessModes.length;
    const nextMode = accessModes[next] ?? "public_waitlist";
    setMode(nextMode);
    requestAnimationFrame(() => document.getElementById(tabId(nextMode))?.focus());
  };

  const heading = mode === "public_waitlist"
    ? "Join the public waitlist"
    : mode === "invite_beta"
      ? "Request beta access"
      : "Use your invite";
  const description = mode === "public_waitlist"
    ? "Be first to hear when the research workspace opens more broadly."
    : mode === "invite_beta"
      ? "Use this route if you actively build or test Python trading strategies."
      : "Enter your one-time code. You’ll create your account on the next screen.";

  return (
    <section className="chapter access waitlist-experience" id="access">
      <div className="chapter-frame waitlist-experience__layout">
        <div className="waitlist-experience__intro">
          <div className="waitlist-experience__eyebrow">
            <span aria-hidden="true"><i /><i /></span>
            {BRAND.name} / Early access
          </div>
          <h2>Get a place at the workbench.</h2>
          <p>Join the public list, make a case for early access, or use an invite to create your account.</p>
        </div>

        <div className={`waitlist-card waitlist-card--liquid-experiment waitlist-card--${mode}`}>
          <div className="waitlist-card__tabs" role="tablist" aria-label="Access route">
            {accessModes.map((accessMode) => (
              <button
                aria-controls={`${baseId}-panel`}
                aria-selected={mode === accessMode}
                id={tabId(accessMode)}
                key={accessMode}
                onClick={() => setMode(accessMode)}
                onKeyDown={moveTab}
                role="tab"
                tabIndex={mode === accessMode ? 0 : -1}
              >
                {accessMode === "public_waitlist" ? <Users aria-hidden="true" size={16} /> : accessMode === "invite_beta" ? <FlaskConical aria-hidden="true" size={16} /> : <Link2 aria-hidden="true" size={16} />}
                {accessMode === "public_waitlist" ? "Waitlist" : accessMode === "invite_beta" ? "Beta request" : "Use invite"}
              </button>
            ))}
          </div>

          <div aria-labelledby={tabId(mode)} id={`${baseId}-panel`} role="tabpanel">
            <div className="waitlist-card__intro">
              <h3>{heading}</h3>
              <p>{description}</p>
            </div>
            {mode === "redeem" ? <RedeemInviteForm /> : <AccessSignupForm key={mode} mode={mode} />}
          </div>

          <div className="waitlist-card__status" aria-label="Access status">
            <span><i aria-hidden="true" /> Invite-only beta</span>
            <span>Launch timing shared with confirmed signups</span>
          </div>
        </div>
      </div>
    </section>
  );
}
